import { useMemo } from 'react';
import type { ProductDto } from '@/types/dtos/orders/response/product.dto.ts';
import { Avatar, Stack, Tooltip, Typography } from '@mui/material';
import type { EventDto } from '@/types/dtos/events/response/event.dto.ts';
import { roundToTwoDecimals } from '@/helpers/decimal.helper.ts';
import { useTranslations } from '@/i18n/use-translations.tsx';
import { useQuotaBalances } from '@/state/queries/entitlements/use-quota-balances';
import {
  getQuotaAvatarTooltipTranslationKey,
  getQuotaKeyForCatalogAvatar,
  isQuotaAvatarVisible,
  resolveQuotaScopeForEvent,
} from '@/helpers/product-quota.helper';
import { getProductCatalogQuotaIcon } from '@/constants/product-catalog-icons.tsx';

export default function EventCatalogProductQuotas({ product, event }: { product?: ProductDto; event?: EventDto }) {
  const t = useTranslations();
  const { scopeType: quotaScopeType, scopeId } = resolveQuotaScopeForEvent(event);
  const { data: balances, isPending, isError } = useQuotaBalances(quotaScopeType, scopeId);

  const { quotaQuantityLabel, tooltipText, backgroundColor, borderColor } = useMemo(() => {
    if (!event || !product) {
      return {
        quotaQuantityLabel: '0/0',
        tooltipText: '',
        backgroundColor: 'primary.main',
        borderColor: 'primary.main',
      };
    }

    if (isPending && balances === undefined) {
      return {
        quotaQuantityLabel: '…',
        tooltipText: '',
        backgroundColor: 'custom.grey',
        borderColor: 'primary.main',
      };
    }

    if (isError) {
      return {
        quotaQuantityLabel: '—',
        tooltipText: '',
        backgroundColor: 'custom.grey',
        borderColor: 'primary.main',
      };
    }

    const quotaKey = getQuotaKeyForCatalogAvatar(product);
    const row = quotaKey ? balances?.find((b) => b.quotaKey === quotaKey) : undefined;
    const tooltipKey = getQuotaAvatarTooltipTranslationKey(product);

    if (row && row.totalGranted > 0) {
      const usedQuantity = roundToTwoDecimals(row.totalConsumed);
      const bookedQuantity = row.totalGranted;
      const quantities = `${usedQuantity}/${bookedQuantity}`;

      const tooltip = t(tooltipKey, { used: usedQuantity.toString(), total: bookedQuantity.toString() });

      const bgColor = 'primary.main';
      let bdColor = 'primary.main';
      const usedPercentage = bookedQuantity > 0 ? (usedQuantity / bookedQuantity) * 100 : 0;
      if (usedPercentage >= 100) {
        bdColor = 'error.main';
      } else if (usedPercentage >= 80) {
        bdColor = 'info.main';
      }

      return {
        quotaQuantityLabel: quantities,
        tooltipText: tooltip,
        backgroundColor: bgColor,
        borderColor: bdColor,
      };
    }

    return {
      quotaQuantityLabel: '0/0',
      tooltipText: t(tooltipKey, { used: '0', total: '0' }),
      backgroundColor: 'custom.grey',
      borderColor: 'primary.main',
    };
  }, [event, product, balances, t, isPending, isError]);

  if (product && !isQuotaAvatarVisible(product)) {
    return null;
  }

  return (
    <Tooltip title={tooltipText}>
      <Avatar
        variant="rounded"
        sx={{
          width: '40px',
          minWidth: '40px',
          height: '40px',
          color: 'white',
          fontSize: '0.8rem',
          backgroundColor: backgroundColor,
          border: '2px solid',
          borderColor: borderColor,
        }}
      >
        <Stack alignItems="center" justifyContent="center">
          {product && getProductCatalogQuotaIcon(product.key)}
          <Typography color="white" variant="body2" sx={{ fontSize: '.8em' }}>
            {quotaQuantityLabel}
          </Typography>
        </Stack>
      </Avatar>
    </Tooltip>
  );
}
